import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { useLicense } from "@/hooks/useLicense";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

type SessionInfo = {
  sessionId: string;
  status: string | null;
  paymentStatus: string | null;
  customerEmail: string | null;
  amountTotal: number | null;
  currency: string | null;
  userId: string | null;
};

type VerifyState =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "error"; message: string }
  | { kind: "ok"; session: SessionInfo };

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, loading: authLoading } = useAuth();
  const { hasLicense } = useLicense(user?.id);
  const [verify, setVerify] = useState<VerifyState>({ kind: "loading" });

  useEffect(() => {
    if (!sessionId) {
      setVerify({ kind: "missing" });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.functions.invoke("verify-checkout-session", {
        body: { sessionId, environment: getStripeEnvironment() },
      });
      if (cancelled) return;
      if (error || !data || data.error) {
        setVerify({ kind: "error", message: error?.message || data?.error || "Could not verify session" });
        return;
      }
      setVerify({ kind: "ok", session: data as SessionInfo });
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (hasLicense && verify.kind === "ok" && verify.session.paymentStatus === "paid") {
      trackEvent(ANALYTICS_EVENTS.CHECKOUT_SUCCESS, {
        amount: verify.session.amountTotal ?? 0,
        currency: verify.session.currency ?? "",
      });
      const t = setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [hasLicense, verify, navigate]);

  const paid = verify.kind === "ok" && verify.session.paymentStatus === "paid";
  const amount =
    verify.kind === "ok" && verify.session.amountTotal != null && verify.session.currency
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: verify.session.currency.toUpperCase(),
        }).format(verify.session.amountTotal / 100)
      : null;

  return (
    <>
      <Helmet>
        <title>Checkout result — Trends Discovery</title>
        <meta name="description" content="Confirming your Trends Discovery purchase. Your lifetime license is activated immediately on successful payment." />
        <link rel="canonical" href="https://trendsdiscovery.com/checkout/success" />
        <meta property="og:title" content="Checkout result — Trends Discovery" />
        <meta property="og:url" content="https://trendsdiscovery.com/checkout/success" />
        <meta name="robots" content="noindex" />
      </Helmet>
    <div className="min-h-screen grid place-items-center px-4 bg-hero-radial">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-10 text-center shadow-elevated">
        <div className="font-mono text-xs text-primary mb-3">// CHECKOUT RESULT</div>

        {verify.kind === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
            <h1 className="font-display text-3xl font-bold mt-4">Verifying your payment…</h1>
          </>
        )}

        {verify.kind === "missing" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-destructive" />
            <h1 className="font-display text-3xl font-bold mt-4">No session found</h1>
            <p className="mt-3 text-muted-foreground text-sm">
              We couldn't find a checkout session in the URL.
            </p>
          </>
        )}

        {verify.kind === "error" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-destructive" />
            <h1 className="font-display text-3xl font-bold mt-4">Verification failed</h1>
            <p className="mt-3 text-muted-foreground text-sm">{verify.message}</p>
          </>
        )}

        {verify.kind === "ok" && (
          <>
            {paid ? (
              <CheckCircle2 className="w-10 h-10 mx-auto text-primary" />
            ) : (
              <Loader2 className="w-10 h-10 mx-auto text-muted-foreground animate-spin" />
            )}
            <h1 className="font-display text-3xl font-bold mt-4">
              {paid ? "You're in." : "Payment processing…"}
            </h1>

            <dl className="mt-6 grid grid-cols-2 gap-y-2 text-left text-sm font-mono">
              <dt className="text-muted-foreground">session</dt>
              <dd className="truncate" title={verify.session.sessionId}>
                {verify.session.sessionId.slice(0, 18)}…
              </dd>
              <dt className="text-muted-foreground">status</dt>
              <dd>{verify.session.status ?? "—"}</dd>
              <dt className="text-muted-foreground">payment</dt>
              <dd>{verify.session.paymentStatus ?? "—"}</dd>
              {amount && (
                <>
                  <dt className="text-muted-foreground">amount</dt>
                  <dd>{amount}</dd>
                </>
              )}
              {verify.session.customerEmail && (
                <>
                  <dt className="text-muted-foreground">email</dt>
                  <dd className="truncate">{verify.session.customerEmail}</dd>
                </>
              )}
            </dl>

            <div className="mt-6 rounded-lg border border-border bg-background/50 p-4 text-sm">
              <div className="font-mono text-[11px] text-primary mb-1">// LICENSE STATUS</div>
              {authLoading ? (
                <span className="text-muted-foreground">Loading account…</span>
              ) : hasLicense ? (
                <span className="text-primary">Active — redirecting to dashboard…</span>
              ) : paid ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse-dot" />
                  Provisioning your license…
                </span>
              ) : (
                <span className="text-muted-foreground">Waiting on payment confirmation.</span>
              )}
            </div>
          </>
        )}

        <div className="mt-8">
          <Link to="/dashboard" className="text-sm underline text-muted-foreground">
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}
